import React, { useState, useRef, useEffect } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { auth, db } from "../firebase"; 
import { 
  signInWithEmailAndPassword, 
  signOut, 
  setPersistence, 
  browserSessionPersistence,
  sendPasswordResetEmail 
} from "firebase/auth";
import { 
  doc, getDoc, setDoc, updateDoc, collection, addDoc, serverTimestamp, increment
} from "firebase/firestore"; 
import "./ExpertLogin.css";
import logoImg from "./Sorted_Logo.png";

// NOTE — this lockout is client-enforced only: the counter and lock flag live
// in Firestore and are written by the browser's own SDK, so it deters a casual
// mistyped password but not an attacker calling Firebase Auth's REST API
// directly (that bypasses this component entirely). Real brute-force
// protection needs to live server-side — e.g. Firebase App Check, or a Cloud
// Function trigger on failed sign-in attempts — which is outside what a
// frontend file alone can enforce.
const MAX_FAILED_ATTEMPTS = 3;
const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.thesorted.co.in";

function ExpertLogin({ onRegisterClick, onLoginSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // States for the custom Forgot Password Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [modalError, setModalError] = useState("");
  const [modalSuccess, setModalSuccess] = useState("");
  const [isSendingReset, setIsSendingReset] = useState(false);

  // ═══════════════════════════════════════════════════════════════════
  // BROWSER / DEVICE BACK-BUTTON SUPPORT
  // The Forgot Password modal is just React state (isModalOpen) sitting on
  // top of this page, so without this the back button has nothing to
  // undo and just leaves the site. Push a history entry when it opens so
  // one back press closes the modal instead.
  // ═══════════════════════════════════════════════════════════════════
  const isPopNavRef = useRef(false);
  const modalWasOpenRef = useRef(false);

  useEffect(() => {
    if (isPopNavRef.current) { modalWasOpenRef.current = isModalOpen; return; }
    if (isModalOpen && !modalWasOpenRef.current) {
      window.history.pushState({ expertLoginModal: true }, "");
    }
    modalWasOpenRef.current = isModalOpen;
  }, [isModalOpen]);

  useEffect(() => {
    const handlePopState = (e) => {
      isPopNavRef.current = true;
      if (!e.state || !e.state.expertLoginModal) {
        setIsModalOpen(false);
      }
      setTimeout(() => { isPopNavRef.current = false; }, 0);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const sanitizeEmailForId = (rawEmail) => rawEmail.toLowerCase().trim().replace(/[^a-z0-9]/g, "_");

  // Writes the start of a session to experts/{expertId}/sessions — the
  // time-on-site tracking subcollection from the schema. This only records
  // the START event (startedAt + device); ending the session and computing
  // durationSeconds needs to happen wherever the expert app actually detects
  // logout/unload (e.g. ExpertDashboard.js's onLogout handler, or a
  // `beforeunload` listener there) — that's a separate change, out of scope
  // for this login file, since ExpertLogin unmounts immediately after this
  // call and never sees the session end. Failure here is deliberately
  // swallowed — a lost analytics write should never block someone signing in.
  const logSessionStart = async (expertId) => {
    try {
      const sessionRef = await addDoc(collection(db, "experts", expertId, "sessions"), {
        startedAt: serverTimestamp(),
        device: navigator.userAgent || "unknown",
      });
      // Stashed so a future logout handler elsewhere in the app can look up
      // and close out this exact session doc instead of guessing which one.
      sessionStorage.setItem("sorted_expert_session_id", sessionRef.id);
    } catch (err) {
      console.error("Failed to log session start:", err);
    }

    // Write flat login tracking fields on the expert doc alongside the
    // session subcollection entry. Enables "last login" display and
    // "login count" queries without reading the sessions subcollection.
    // Non-fatal — failure here must never block the login.
    try {
      await updateDoc(doc(db, "experts", expertId), {
        lastLoginAt: serverTimestamp(),
        loginCount:  increment(1),
      });
    } catch (err) {
      console.error("Failed to record expert login timestamp:", err);
    }
  };

  const getSecurityDoc = async (emailToFind) => {
    const ref = doc(db, "loginSecurity", sanitizeEmailForId(emailToFind));
    const snap = await getDoc(ref);
    return { ref, data: snap.exists() ? snap.data() : {} };
  };

  const handleFailedAttempt = async (trimmedEmail) => {
    try {
      const { ref, data } = await getSecurityDoc(trimmedEmail);
      const currentAttempts = (data.failedLoginAttempts || 0) + 1;
      const now = serverTimestamp();
      // Auto-unlock after 2 hours — lockExpiresAt enables time-window reset
      const lockExpiresAt = new Date(Date.now() + 2 * 60 * 60 * 1000);

      if (currentAttempts >= MAX_FAILED_ATTEMPTS) {
        await setDoc(ref, {
          failedLoginAttempts: 0,
          accountLocked:       true,
          lastAttemptAt:       now,
          lockedAt:            now,
          lockExpiresAt,
        }, { merge: true });
        
        await sendPasswordResetEmail(auth, trimmedEmail);
        setErrorMessage("🔒 Your account is blocked due to 3 failed password attempts. A reset link has been sent to your email.");
      } else {
        await setDoc(ref, {
          failedLoginAttempts: currentAttempts,
          lastAttemptAt:       now,
        }, { merge: true });
        const remaining = MAX_FAILED_ATTEMPTS - currentAttempts;
        setErrorMessage(`❌ Invalid email address or password. ${remaining} attempt${remaining === 1 ? "" : "s"} remaining before your account is locked.`);
      }
    } catch (trackingError) {
      console.error("Failed attempt tracking error:", trackingError);
      setErrorMessage("❌ Invalid email address or password.");
    }
  };

  // Triggered inside the new Pop-up view
  const handleModalResetSubmit = async (e) => {
    e.preventDefault();

    // Guard against multi-traffic: block re-entry if a reset request is already in flight
    if (isSendingReset) return;

    setModalError("");
    setModalSuccess("");

    if (!resetEmail.trim()) {
      setModalError("⚠️ Please enter an email address.");
      return;
    }

    const trimmedResetEmail = resetEmail.trim();
    setIsSendingReset(true);

    // Note on email enumeration: this used to check Firestore first and show a
    // distinct "this email is not registered" error, which let anyone probe
    // which addresses have expert accounts. Now the same generic success
    // message is shown whether or not the account exists — including when
    // Firebase Auth itself reports auth/user-not-found below — and the account
    // lock is only cleared once the reset actually succeeds against a real
    // account, so a non-existent email can't be used to reset anything either.
    try {
      await sendPasswordResetEmail(auth, trimmedResetEmail);

      const securityCheckRef = doc(db, "loginSecurity", sanitizeEmailForId(trimmedResetEmail));
      await setDoc(securityCheckRef, {
        accountLocked:       false,
        failedLoginAttempts: 0,
        lockedAt:            null,
        lockExpiresAt:       null,
      }, { merge: true });

      setModalSuccess("📨 If that email is registered, a password reset link has been sent. Check your inbox.");
      setResetEmail("");
    } catch (err) {
      console.error("Password reset error:", err.code, err.message);

      switch (err.code) {
        case "auth/user-not-found":
          // Deliberately the same copy as the success case above — this is the
          // one place enumeration would otherwise leak through.
          setModalSuccess("📨 If that email is registered, a password reset link has been sent. Check your inbox.");
          setResetEmail("");
          break;
        case "auth/invalid-email":
          setModalError("⚠️ Please enter a valid email address.");
          break;
        case "auth/too-many-requests":
          setModalError("⏳ Too many requests. Please wait a moment and try again.");
          break;
        case "auth/network-request-failed":
          setModalError("📡 Network error. Please check your connection and try again.");
          break;
        default:
          setModalError("❌ Something went wrong. Please try again.");
      }
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Guard against multi-traffic: block re-entry if a submit is already in flight
    if (isLoggingIn) return;

    setErrorMessage("");
    
    if (!email.trim() || !password) {
      setErrorMessage("⚠️ Please fill in all account validation inputs.");
      return;
    }

    const trimmedEmail = email.trim();
    setIsLoggingIn(true);

    let securityRef = null;
    try {
      const { ref, data } = await getSecurityDoc(trimmedEmail);
      securityRef = ref;
      
      if (data.accountLocked) {
        // FIX: Check whether the 2-hour lock window has expired.
        // Previously lockExpiresAt was stored but never read — accounts were
        // permanently locked until the expert used Forgot Password.
        const expiresRaw = data.lockExpiresAt;
        const expiresAt  = expiresRaw?.toDate ? expiresRaw.toDate() : expiresRaw ? new Date(expiresRaw) : null;
        if (expiresAt && expiresAt < new Date()) {
          // Lock window expired — clear it and allow the login attempt to proceed
          await setDoc(ref, {
            accountLocked:       false,
            failedLoginAttempts: 0,
            lockExpiresAt:       null,
          }, { merge: true }).catch(() => {});
          // fall through to the normal Firebase signIn below
        } else {
          setErrorMessage("🔒 This account is blocked. Please use the Forgot Password option to reset it and regain access.");
          setIsLoggingIn(false);
          return;
        }
      }
    } catch (lockCheckError) {
      console.warn("Lockout check skipped:", lockCheckError);
      securityRef = null;
    }

    try {
      await setPersistence(auth, browserSessionPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, trimmedEmail, password);
      const user = userCredential.user;

      if (securityRef) {
        await setDoc(securityRef, {
          failedLoginAttempts: 0,
          accountLocked:       false,
          lockedAt:            null,
          lockExpiresAt:       null,
        }, { merge: true });
      }

      const docRef = doc(db, "experts", user.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const expertData = docSnap.data();
        // approvalStatus is the single field every file in this app reads/writes
        // for an expert's review state (signup, login, session restore, dashboard,
        // and the admin panel all use it now — a separate "status" field used to
        // exist alongside it and drift out of sync, which meant a newly-approved
        // expert could never actually pass this gate).
        const currentStatus = (expertData.approvalStatus || "").toLowerCase().trim();

        if (currentStatus === "deactivated") {
          // Let a deactivated expert log in — the dashboard's reactivation flow
          // moves them from "deactivated" back to "pending" so they can re-verify.
          logSessionStart(user.uid);
          setIsLoggingIn(false);
          if (onLoginSuccess) onLoginSuccess(expertData);
          return;
        }

        if (currentStatus === "pending" || currentStatus === "rejected" || currentStatus === "deleted") {
          setErrorMessage(
            currentStatus === "pending"
              ? "⏳ Your expert registration application is currently under admin review."
              : currentStatus === "rejected"
              ? "🛑 Your application has been rejected by the verification board."
              : "🛑 This account has been closed. Please contact support if this is unexpected."
          );
          await signOut(auth);
          setIsLoggingIn(false);
          return;
        }

        logSessionStart(user.uid);
        setIsLoggingIn(false);
        if (onLoginSuccess) onLoginSuccess(expertData);
      } else {
        // No expert doc — use backend cross-portal check (checks both UID and email).
        // Previously only checked users/{uid} by UID — no email fallback.
        // Backend checks both and returns a clear message for each case.
        try {
          const idToken = await user.getIdToken();
          const checkRes = await fetch(`${API_BASE_URL}/api/auth/cross-portal-check`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
            body: JSON.stringify({ portal: "expert" }),
          });
          const checkData = await checkRes.json().catch(() => ({}));
          if (checkData.allowed === false) {
            setErrorMessage(`🛑 ${checkData.reason || "This email is registered as a user account. Please use the main SORTED app."}`);
          } else {
            setErrorMessage("🛑 Expert profile not found. Please register as an expert first.");
          }
        } catch {
          // Backend call failed — fall back to direct Firestore check
          try {
            const userSnap = await getDoc(doc(db, "users", user.uid));
            setErrorMessage(userSnap.exists()
              ? "🛑 This email is registered as a user account. Please use the main Sorted login page."
              : "🛑 Expert profile not found. Please register as an expert first."
            );
          } catch {
            setErrorMessage("🛑 Expert profile not found. Please register as an expert first.");
          }
        }
        await signOut(auth);
        setIsLoggingIn(false);
      }
    } catch (error) {
      setIsLoggingIn(false);

      switch (error.code) {
        case "auth/user-not-found":
          setErrorMessage("❌ No account exists with this email. Please register first.");
          break;

        case "auth/wrong-password":
        case "auth/invalid-credential":
          await handleFailedAttempt(trimmedEmail);
          break;

        case "auth/invalid-email":
          setErrorMessage("⚠️ Please enter a valid email address.");
          break;

        case "auth/too-many-requests":
          setErrorMessage("⏳ Too many attempts. Please wait a moment and try again.");
          break;

        case "auth/user-disabled":
          setErrorMessage("🔒 This account has been disabled. Please contact support.");
          break;

        case "auth/network-request-failed":
          setErrorMessage("📡 Network error. Please check your connection and try again.");
          break;

        default:
          // Never show raw Firebase error text to the user
          console.error("Unhandled auth error:", error.code, error.message);
          setErrorMessage("❌ Something went wrong while signing in. Please try again.");
      }
    }
  };

  return (
    <div className="glass-login-page">
      <div className="glass-mesh-gradient-glow"></div>
      <div className="glass-login-container">
        
        <div className="glass-login-logo-header">
          <div className="glass-login-logo-inner-row">
            <img src={logoImg} alt="SORTED Logo" className="glass-logo-img-asset" />
            <div className="glass-logo-text-stack">
              <span className="glass-brand-title">SORTED</span>
            </div>
          </div>
          <span className="glass-logo-sub">Expert Portal</span>
        </div>

        <div className="glass-login-card">
          <h2>Welcome back</h2>
          <p className="glass-card-subtitle">Sign in to your expert dashboard</p>

          <form onSubmit={handleSubmit} className="glass-login-form">
            <div className="glass-input-group">
              <label>Email Address</label>
              <input 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                placeholder="rahul@sharmaconsulting.com"
                autoComplete="username"
                className="glass-form-input"
              />
            </div>

            <div className="glass-input-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label>Password</label>
                <span 
                  onClick={() => {
                    setModalError("");
                    setModalSuccess("");
                    setIsModalOpen(true);
                  }} 
                  className="glass-link-trigger" 
                  style={{ fontSize: '13px' }}
                >
                  Forgot Password?
                </span>
              </div>
              <div className="glass-relative-parent">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="glass-form-input glass-password-input-padded"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="glass-password-toggle-btn"
                  tabIndex={-1}
                >
                  {showPassword ? <FiEyeOff size={17} /> : <FiEye size={17} />}
                </button>
              </div>
            </div>

            {errorMessage && <p className="glass-login-error-msg">{errorMessage}</p>}

            <button type="submit" className="glass-submit-btn" disabled={isLoggingIn}>
              {isLoggingIn ? "Verifying Credentials..." : "Sign In to Dashboard"}
            </button>
          </form>

          <p className="glass-register-redirect">
            New to SORTED? <span className="glass-link-trigger" onClick={onRegisterClick}>Register as Expert</span>
          </p>
        </div>
      </div>

      {/* POP-UP FORGOT PASSWORD MODULE */}
      {isModalOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
          backgroundColor: "rgba(5, 8, 16, 0.85)", backdropFilter: "blur(12px)",
          display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999
        }}>
          <div className="glass-login-card" style={{ maxWidth: "400px", position: "relative" }}>
            <span 
              onClick={() => setIsModalOpen(false)}
              style={{ position: "absolute", top: "16px", right: "20px", color: "#94a3b8", cursor: "pointer", fontSize: "20px" }}
            >
              ✕
            </span>
            <h2 style={{ fontSize: "20px", marginBottom: "8px" }}>Reset Password</h2>
            <p className="glass-card-subtitle" style={{ marginBottom: "20px" }}>Enter your email to receive a recovery link.</p>
            
            <form onSubmit={handleModalResetSubmit} className="glass-login-form">
              <div className="glass-input-group">
                <label>Email Address</label>
                <input 
                  type="email" 
                  value={resetEmail} 
                  onChange={(e) => setResetEmail(e.target.value)} 
                  placeholder="name@company.com"
                  className="glass-form-input"
                />
              </div>

              {modalError && <p className="glass-login-error-msg">{modalError}</p>}
              {modalSuccess && <p className="glass-login-error-msg" style={{ color: '#4ade80' }}>{modalSuccess}</p>}

              <button type="submit" className="glass-submit-btn" disabled={isSendingReset}>
                {isSendingReset ? "Processing link..." : "Send Password Reset Link"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExpertLogin;